import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type ScrollingTitleProps = {
  title: string;
  /** Typography classes applied to the text */
  className?: string;
};

export default function ScrollingTitle({
  title,
  className,
}: ScrollingTitleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const animRef = useRef<Animation | null>(null);
  const [offset, setOffset] = useState(0);

  // Measure how much the text overflows the container
  useEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const ghost = measureRef.current;
      if (!container || !ghost) return;
      // ghost is position:fixed — its offsetWidth is the true natural text width,
      // free from any overflow/flex constraints on the container.
      setOffset(Math.max(0, ghost.offsetWidth - container.offsetWidth));
    };

    measure();
    if ("fonts" in document) {
      document.fonts.ready.then(measure);
    }

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      if (containerRef.current) ro.observe(containerRef.current);
      return () => ro.disconnect();
    }

    // Fallback: re-measure on window resize.
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [title, className]);

  // Drive the scroll animation via Web Animations API so the exact pixel offset
  // is baked in as a number — no CSS custom property / var() resolution needed.
  useEffect(() => {
    animRef.current?.cancel();
    animRef.current = null;

    const text = textRef.current;
    if (!text || offset <= 0) return;

    animRef.current = text.animate(
      [
        { transform: "translateX(0px)", offset: 0 },
        { transform: "translateX(0px)", offset: 0.15 },
        { transform: `translateX(-${offset}px)`, offset: 0.85 },
        { transform: `translateX(-${offset}px)`, offset: 1 },
      ],
      {
        duration: 6000,
        iterations: Infinity,
        direction: "alternate",
        easing: "ease-in-out",
      },
    );

    return () => {
      animRef.current?.cancel();
    };
  }, [offset, title, className]);

  return (
    <>
      {/* Off-screen ghost — position:fixed gives its true rendered width */}
      <span
        ref={measureRef}
        aria-hidden
        className={cn("whitespace-nowrap", className)}
        style={{
          position: "fixed",
          top: "-9999px",
          left: "-9999px",
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        {title}
      </span>

      <div
        ref={containerRef}
        className="flex-1 min-w-0 overflow-hidden pb-4 -mb-4"
      >
        <span
          ref={textRef}
          className={cn("inline-block whitespace-nowrap", className)}
        >
          {title}
        </span>
      </div>
    </>
  );
}
